import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((i) => i.id === invoice.id);
      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((i) => i.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;

    const invoiceIndex = state.findIndex((i) => i.id === invoiceId);
    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Invoices = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam,] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isCompanyExpired, setIsCompanyExpired] = useState(false);
  const [companyPlans, setCompanyPlans] = useState({}); // Para cachear os planos das empresas
  const [messageVisible, setMessageVisible] = useState(true); // Para controlar se a mensagem está visível

  // Verificar se a empresa está vencida
  useEffect(() => {
    if (user && user.company) {
      const hoje = moment();
      const vencimento = moment(user.company.dueDate);
      const isExpired = hoje.isAfter(vencimento);
      setIsCompanyExpired(isExpired);
    }
  }, [user]);

  const handleOpenContactModal = (invoices) => {
    setStoragePlans(invoices);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          console.log("Buscando faturas...", { searchParam, pageNumber });
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });

          console.log("Dados recebidos:", data);
          dispatch({ type: "LOAD_INVOICES", payload: data });
          console.log("Dispatch realizado com payload:", data);
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          console.error("Erro ao buscar faturas:", err);
          toastError(err);
          setLoading(false);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const rowStyle = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    if (dias < 0 && record.status !== "paid") {
      return { backgroundColor: "#ffbcbc9c" };
    }
  };

  const rowStatus = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    const status = record.status;
    if (status === "paid") {
      return "Pago";
    }
    if (dias < 0) {
      return "Vencido";
    } else {
      return "Em Aberto"
    }
  }

  // Função para converter valor brasileiro (com vírgula) para número
  const parseValueBR = (value) => {
    if (typeof value === 'number') return value;
    if (!value) return 0;

    // Converte string brasileira "1,50" para "1.50" e depois para número
    const cleanValue = value.toString().replace(',', '.');
    const numValue = parseFloat(cleanValue);

    console.log(`🔢 [FATURAS] Convertendo valor: "${value}" → ${numValue}`);

    return isNaN(numValue) ? 0 : numValue;
  };

  // Função para buscar o plano atual de uma empresa
  const fetchCompanyPlan = async (companyId) => {
    try {
      console.log(`💰 [FATURAS] Buscando plano para empresa: ${companyId}`);

      const { data } = await api.get(`/companies/listPlan/${companyId}`);

      if (data && data.plan && data.plan.amount) {
        console.log(`📊 [FATURAS] Plano encontrado:`, {
          empresa: companyId,
          plano: data.plan.name,
          valor: data.plan.amount
        });

        setCompanyPlans(prev => ({
          ...prev,
          [companyId]: data.plan.amount
        }));

        return data.plan.amount;
      }
    } catch (error) {
      console.error(`❌ [FATURAS] Erro ao buscar plano da empresa ${companyId}:`, error);
    }
    return null;
  };

  // Função para renderizar o valor atual do plano ou da fatura
  const renderCurrentValue = (invoice) => {
    const companyId = invoice.companyId;
    const currentPlanValue = companyPlans[companyId];
    const invoiceValue = invoice.value;

    // Converter valores usando a função brasileira
    const currentPlanValueNum = parseValueBR(currentPlanValue);
    const invoiceValueNum = parseValueBR(invoiceValue);

    // Se temos o valor atual do plano e é diferente da fatura
    if (currentPlanValue !== undefined && currentPlanValueNum !== invoiceValueNum) {
      console.log(`🔄 [FATURAS] Valor desatualizado detectado:`, {
        fatura: invoice.id,
        empresa: companyId,
        valorFatura: invoiceValue,
        valorFaturaNum: invoiceValueNum,
        valorPlanoAtual: currentPlanValue,
        valorPlanoAtualNum: currentPlanValueNum
      });

      return (
        <div>
          <div style={{ color: '#2196f3', fontWeight: 'bold' }}>
            {currentPlanValueNum.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
          </div>
          <div style={{ fontSize: '0.8em', color: '#999', textDecoration: 'line-through' }}>
            {invoiceValueNum.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
          </div>
        </div>
      );
    }

    // Caso padrão: mostrar valor da fatura
    return invoiceValueNum.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
  };

  // Buscar planos das empresas quando faturas carregam
  useEffect(() => {
    if (invoices.length > 0) {
      const uniqueCompanyIds = [...new Set(invoices.map(inv => inv.companyId))];

      uniqueCompanyIds.forEach(companyId => {
        if (!companyPlans[companyId]) {
          fetchCompanyPlan(companyId);
        }
      });
    }
  }, [invoices, companyPlans]);

  const renderUseWhatsapp = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseFacebook = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseInstagram = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseCampaigns = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseSchedules = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseInternalChat = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseExternalApi = (row) => { return row.status === false ? "Não" : "Sim" };

  // Função para verificar manualmente o status do PIX
  const checkPixStatus = async (invoice) => {
    if (!invoice.txid) {
      alert("Esta fatura não possui um TXID de PIX para verificar.");

      // Se não tem TXID mas é da fatura 1, usar o TXID conhecido dos logs
      if (invoice.id === 1) {
        const knownTxid = '2a927727d4f148849363542716e7cc75';
        const useKnownTxid = window.confirm(`Esta fatura não tem TXID salvo, mas encontramos o TXID dos logs: ${knownTxid}. Usar este TXID para verificar?`);
        if (!useKnownTxid) {
          return;
        }
        invoice.txid = knownTxid;
      } else {
        return;
      }
    }

    try {
      setLoading(true);
      console.log(`🔍 [CHECK PIX] Verificando status para fatura ${invoice.id} com TXID: ${invoice.txid}`);

      const { data } = await api.post('/subscription/check-pix-status', {
        txid: invoice.txid,
        invoiceId: invoice.id
      });

      if (data.success) {
        if (data.processed) {
          alert('✅ Pagamento processado com sucesso! A página será recarregada para atualizar os dados.');
          window.location.reload();
        } else if (data.alreadyProcessed) {
          alert('ℹ️ Este pagamento já foi processado anteriormente.');
        } else {
          alert(`📋 Status atual: ${data.message}`);
        }
      } else {
        alert(`❌ Erro ao verificar status: ${data.error}`);
        console.error('Detalhes do erro:', data.details);
      }
    } catch (error) {
      console.error('❌ [CHECK PIX] Erro ao verificar status:', error);
      alert('❌ Erro ao verificar status do PIX. Verifique o console para mais detalhes.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer>
      <style>
        {`
          @keyframes pulse {
            0% { transform: translate(-50%, -50%) scale(1); }
            50% { transform: translate(-50%, -50%) scale(1.02); }
            100% { transform: translate(-50%, -50%) scale(1); }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
        `}
      </style>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}

      ></SubscriptionModal>
      <MainHeader>
        <Title>Faturas ({invoices.length})</Title>
      </MainHeader>

      {/* Alerta sobre verificação manual de PIX */}
      {invoices.some(inv => rowStatus(inv) !== "Pago") && (
        <div style={{
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          padding: '16px',
          marginBottom: '16px',
          borderRadius: '8px',
          border: '1px solid #2196f3',
          textAlign: 'center'
        }}>
          <strong>💡 DICA PIX:</strong> Se você fez um PIX recentemente que não foi processado automaticamente,
          use o botão <strong>"✓ VERIFICAR PIX"</strong> na coluna de ações para processar manualmente.
          <br />
          <small style={{ opacity: 0.8 }}>
            (O webhook pode não funcionar com localhost/desenvolvimento)
          </small>
        </div>
      )}

      {isCompanyExpired && messageVisible && (
        <>
          {/* Overlay de fundo escuro */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            backdropFilter: 'blur(3px)'
          }}></div>

          {/* Modal centralizado */}
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '75%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            width: '50%',
            // maxWidth: '520px',
            // minHeight: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              backgroundColor: '#fff8e1',
              color: '#e65100',
              padding: '32px 40px',
              borderRadius: '20px',
              border: '3px solid #ffb300',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              animation: 'pulse 2s infinite',


            }}>
              {/* Botão fechar */}
              <button
                onClick={() => setMessageVisible(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: '#ff6f00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                ×
              </button>

              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                <span style={{
                  backgroundColor: '#ff1100',
                  color: 'white',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  animation: 'bounce 1s infinite'
                }}>!</span>
                Sua assinatura está vencida
              </div>
              <div style={{
                fontSize: '16px',
                color: '#bf360c',
                lineHeight: '1.5',
                marginBottom: '16px'
              }}>
                Entre em contato com o suporte para regularizar sua situação e continuar usando o sistema ou faça o pagamento da fatura pendente clicando no botão "PAGAR" ao lado da fatura vencida.
              </div>
            </div>
          </div>
        </>
      )}

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {/* <TableCell align="center">Id</TableCell> */}
              <TableCell align="center">Detalhes</TableCell>

              <TableCell align="center">Usuários</TableCell>
              <TableCell align="center">Conexões</TableCell>
              <TableCell align="center">Filas</TableCell>
              {/* <TableCell align="center">Whatsapp</TableCell>
              <TableCell align="center">Facebook</TableCell>
              <TableCell align="center">Instagram</TableCell> */}
              {/* <TableCell align="center">Campanhas</TableCell>
              <TableCell align="center">Agendamentos</TableCell>
              <TableCell align="center">Chat Interno</TableCell>
              <TableCell align="center">Rest PI</TableCell> */}

              <TableCell align="center">Valor</TableCell>
              <TableCell align="center">Data Venc.</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {invoices.map((invoices) => (
                <TableRow style={rowStyle(invoices)} key={invoices.id}>
                  {/* <TableCell align="center">{invoices.id}</TableCell> */}
                  <TableCell align="center">{invoices.detail}</TableCell>

                  <TableCell align="center">{invoices.users}</TableCell>
                  <TableCell align="center">{invoices.connections}</TableCell>
                  <TableCell align="center">{invoices.queues}</TableCell>
                  {/* <TableCell align="center">{renderUseWhatsapp(invoices.useWhatsapp)}</TableCell>
                  <TableCell align="center">{renderUseFacebook(invoices.useFacebook)}</TableCell>
                  <TableCell align="center">{renderUseInstagram(invoices.useInstagram)}</TableCell> */}
                  {/* <TableCell align="center">{renderUseCampaigns(invoices.useCampaigns)}</TableCell>
                  <TableCell align="center">{renderUseSchedules(invoices.useSchedules)}</TableCell>
                  <TableCell align="center">{renderUseInternalChat(invoices.useInternalChat)}</TableCell>
                  <TableCell align="center">{renderUseExternalApi(invoices.useExternalApi)}</TableCell> */}

                  <TableCell style={{ fontWeight: 'bold' }} align="center">{renderCurrentValue(invoices)}</TableCell>
                  <TableCell align="center">{moment(invoices.dueDate).format("DD/MM/YYYY")}</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }} align="center">{rowStatus(invoices)}</TableCell>
                  <TableCell align="center">
                    {rowStatus(invoices) !== "Pago" ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleOpenContactModal(invoices)}
                        >
                          PAGAR
                        </Button>
                        {(invoices.txid || invoices.id === 1) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={loading}
                            onClick={() => checkPixStatus(invoices)}
                            style={{ fontSize: '0.7rem', padding: '4px 8px' }}
                            title={invoices.txid ? `TXID: ${invoices.txid}` : 'TXID conhecido dos logs'}
                          >
                            ✓ VERIFICAR PIX
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                      >
                        PAGO
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Invoices;
